import { Formik } from "formik";
import { Link } from "react-router-dom";
import { Button, Checkbox, Form, Message, Table } from "semantic-ui-react";
import MyPaging from "../../app/common/form/MyPaging";
import { formatCurrency } from "../../app/common/util/functions";
import { format } from "date-fns";
import { observer } from "mobx-react-lite";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { CoreTrackingFormValues } from "../../app/models/inventory";
import { useTranslation } from "react-i18next";
import { useStore } from "../../app/stores/store";
import { CoreTrackingIndexDto } from "../../app/models/coreTracking";


type Props = {
    coreTrackingItems: any[];
    listOnClick(value: number): void;
    isSearched: boolean;
}

export default observer(function CoreTrackingIndexTableForm({ coreTrackingItems, listOnClick, isSearched }: Props) {
    const { t } = useTranslation(["common", "inventory"]);
    const { coreTrackingIndexStore } = useStore();
    const {
        pagination,
        pagingParams,
        updateCoreTracking,
        loadingInitial,
        loadCoreTrackingIndex,
    } = coreTrackingIndexStore;

    const cores: CoreTrackingFormValues[] = [];
    for (const c of coreTrackingItems.map(x => x[1])) {
        for (const core of c) {
            cores.push({
                id: core.id,
                totalQty: core.totalQty,
                returned: core.returned,
                refunded: core.refunded,
                isDirty: false
            })
        }
    }

    return (
        <>
            {loadingInitial && isSearched ? (
                <LoadingComponent
                    content={t("coreTrackingIndex_loading", { ns: "inventory" })}
                />
            ) : (
                <Formik
                    initialValues={{
                        cores: cores,
                    }}
                    onSubmit={(values, { setSubmitting, setFieldError, setFieldValue }) => {
                        updateCoreTracking({ cores: values.cores })
                            .then(() => {
                                return loadCoreTrackingIndex();
                            })
                            .catch(err => setFieldError('cores', err.message))
                            .finally(() => {
                                setSubmitting(false);
                            });
                    }}
                >
                    {({ handleSubmit, values, isSubmitting, setFieldValue, errors }) => (
                        <Form
                            className="ui form error"
                            autoComplete="off"
                            onSubmit={handleSubmit}
                        >
                            {errors.cores && (
                                <Message negative>
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: errors.cores,
                                        }}
                                    />
                                </Message>
                            )}
                            {coreTrackingItems.map(vendor => {
                                const name = vendor[0];
                                const items = vendor[1];
                                return (
                            <Table striped unstackable className="dnxTable">
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>
                                            {t("invoicenumber", { ns: "common" })}
                                        </Table.HeaderCell>
                                        <Table.HeaderCell>
                                            {t("labels.invoice_date", { ns: "inventory" })}
                                        </Table.HeaderCell>
                                        <Table.HeaderCell>
                                            {t("labels.partNumber", { ns: "inventory" })}
                                        </Table.HeaderCell>
                                        <Table.HeaderCell>
                                            {t("labels.description", { ns: "inventory" })}
                                        </Table.HeaderCell>
                                        <Table.HeaderCell textAlign="center">
                                            {t("labels.inInventory", { ns: "inventory" })}
                                        </Table.HeaderCell>
                                        <Table.HeaderCell>
                                            {`${t("coreValue", { ns: "inventory" })}`}
                                        </Table.HeaderCell>
                                        <Table.HeaderCell textAlign="right">
                                            {`${t("returned", { ns: "inventory" })}`}
                                        </Table.HeaderCell>
                                        <Table.HeaderCell textAlign="right">
                                            {`${t("refunded", { ns: "inventory" })}`}
                                        </Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    <Table.Row>
                                        <Table.Cell colSpan="8">{name}</Table.Cell>
                                    </Table.Row>
                                    {items &&
                                        items.length ? (
                                        items.map(
                                            (coreTrkItm: CoreTrackingIndexDto) => {
                                                const coreIndex = values.cores.findIndex(c => c.id === coreTrkItm.id);
                                                return (
                                                    <>
                                                        <Table.Row key={`coreTracking.${coreTrkItm.id}`}>
                                                            <Table.Cell
                                                                className="hoveredLink"
                                                                data-label={`${t("invoicenumber", {
                                                                    ns: "common",
                                                                })}:`}
                                                                onClick={() =>
                                                                    window.open(
                                                                        `/ticket/view/${coreTrkItm.ticketId}`,
                                                                        `_`
                                                                    )
                                                                }
                                                            >
                                                                {t("invnum", { ns: "common" })}
                                                                {coreTrkItm.invoiceNumber}
                                                            </Table.Cell>
                                                            <Table.Cell
                                                                data-label={`${t("labels.invoice_date", {
                                                                    ns: "inventory",
                                                                })}:`}
                                                            >
                                                                {coreTrkItm.invoiceDate
                                                                    ? format(
                                                                        new Date(coreTrkItm.invoiceDate),
                                                                        "PP"
                                                                    )
                                                                    : null}
                                                            </Table.Cell>
                                                            <Table.Cell
                                                                className={
                                                                    // eslint-disable-next-line eqeqeq
                                                                    coreTrkItm.inventoryStockId != undefined
                                                                        ? "hoveredLink"
                                                                        : ""
                                                                }
                                                                onClick={() => {
                                                                    // eslint-disable-next-line eqeqeq
                                                                    if (coreTrkItm.inventoryStockId != undefined)
                                                                        window.open(
                                                                            `/inventoryIndex/partDetails/${coreTrkItm.partId}`,
                                                                            "_blank"
                                                                        )
                                                                    }
                                                                }
                                                                data-label={`${t("labels.partNumber", {
                                                                    ns: "inventory",
                                                                })}:`}
                                                            >
                                                                {coreTrkItm.partNumber}
                                                            </Table.Cell>
                                                            <Table.Cell
                                                                data-label={`${t("labels.description", {
                                                                    ns: "inventory",
                                                                })}:`}
                                                            >
                                                                {coreTrkItm.description}
                                                            </Table.Cell>
                                                            <Table.Cell
                                                                data-label={`${t("labels.inInventory", { ns: "inventory" })}:`}
                                                                textAlign="center"
                                                            >
                                                                {
                                                                    // eslint-disable-next-line eqeqeq
                                                                    coreTrkItm.inventoryStockId != undefined 
                                                                        ? t("yes", { ns: "common" }) 
                                                                        : t("no", { ns: "common" })
                                                                }
                                                            </Table.Cell>
                                                            <Table.Cell
                                                                data-label={`${t("coreValue", {
                                                                    ns: "inventory",
                                                                })}:`}
                                                            >
                                                                {formatCurrency(coreTrkItm.coreValue)}
                                                            </Table.Cell>
                                                            <Table.Cell
                                                                width={1}
                                                                textAlign="center"
                                                                data-label={`${t("returned", {
                                                                    ns: "inventory",
                                                                })}:`}
                                                            >
                                                                <Checkbox
                                                                    className="cursor-pointer"
                                                                    name={`returned_${coreTrkItm.id}`}
                                                                    checked={values.cores[coreIndex]?.returned ?? false}
                                                                    onChange={(_, data) => {
                                                                        if (data) {
                                                                            const cpyCores = values.cores.slice();
                                                                            cpyCores[coreIndex].isDirty = true;
                                                                            cpyCores[coreIndex].returned = data.checked;
                                                                            setFieldValue("cores", cpyCores);
                                                                        }
                                                                    }}
                                                                />
                                                            </Table.Cell>
                                                            <Table.Cell
                                                                width={1}
                                                                textAlign="center"
                                                                data-label={`${t("refunded", {
                                                                    ns: "inventory",
                                                                })}:`}
                                                            >
                                                                <Checkbox
                                                                    className="cursor-pointer"
                                                                    name={`refunded_${coreTrkItm.id}`}
                                                                    checked={values.cores[coreIndex]?.refunded ?? false}
                                                                    onChange={(_, data) => {
                                                                        if (data) {
                                                                            const cpyCores = values.cores.slice();
                                                                            cpyCores[coreIndex].isDirty = true;
                                                                            cpyCores[coreIndex].refunded = data.checked;
                                                                            setFieldValue("cores", cpyCores);
                                                                        }
                                                                    }}
                                                                />
                                                            </Table.Cell>
                                                        </Table.Row>
                                                    </>
                                                )
                                            }
                                        )
                                    ) : (
                                        <Table.Row key={"noItems"}>
                                            <Table.Cell width="11" colSpan={11}>
                                                <Message
                                                    color="yellow"
                                                    className="textAlignCenter fontStyleItalic"
                                                >
                                                    {t("grid_noItems", { ns: "common" })}
                                                </Message>
                                            </Table.Cell>
                                        </Table.Row>
                                    )}
                                </Table.Body>
                                <Table.Footer>
                                    <Table.Row>
                                        <Table.Cell colSpan="11">
                                            <MyPaging
                                                itemCount={pagination ? pagination.totalItems : 10}
                                                pageSize={pagingParams.pageSize}
                                                currentPage={pagination ? pagination.currentPage : 1}
                                                onClick={listOnClick}
                                                onChange={listOnClick}
                                                floated="right"
                                            ></MyPaging>
                                        </Table.Cell>
                                    </Table.Row>
                                </Table.Footer>
                            </Table>
                                );
                            })}
                            <Button
                                className="save_button mobile_buttons"
                                loading={isSubmitting || loadingInitial}
                                color="green"
                                content="Save"
                                icon="check"
                                type="submit"
                            />
                            <Link to="/coreTrackingIndex/">
                                <Button
                                    className="save_button mobile_buttons"
                                    as={Link}
                                    to="/inventoryIndex/"
                                    icon="cancel"
                                    type="button"
                                    content={t("cancel", { ns: "common" })}
                                />
                            </Link>
                        </Form>
                    )}
                </Formik>
            )}
        </>
    );
});